import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import React from "react";
import { PastedImage } from "../../hooks/useImagePaste";
import { formatFileSize } from "src/helpers/formatFileSize";

interface PastedImagesPreviewProps {
  images: PastedImage[];
  onRemove: (id: string) => void;
  error?: string | null;
  /** Images still being read; shown as placeholder tiles */
  pendingCount?: number;
}

/**
 * Component to display preview of pasted images in chat input.
 * Shows thumbnails with remove buttons for each image, and a placeholder
 * tile per image still being read.
 */
const PastedImagesPreview: React.FC<PastedImagesPreviewProps> = ({
  images,
  onRemove,
  error,
  pendingCount = 0,
}) => {
  if (images.length === 0 && pendingCount === 0 && !error) {
    return null;
  }

  return (
    <Box data-testid="pasted-images-container" sx={{ px: 1.5, py: 1 }}>
      {error && (
        <Typography
          data-testid="pasted-images-error"
          variant="caption"
          color="error"
          sx={{ display: "block", mb: 1 }}
        >
          {error}
        </Typography>
      )}

      {(images.length > 0 || pendingCount > 0) && (
        <Stack
          data-testid="pasted-images-list"
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1 }}
        >
          {images.map((image, index) => (
            <Box
              key={image.id}
              data-testid={`pasted-image-preview-${index}`}
              sx={{
                position: "relative",
                width: 56,
                height: 56,
                borderRadius: 1.5,
                overflow: "hidden",
                border: "2px solid",
                borderColor: "primary.light",
                bgcolor: "background.paper",
                boxShadow: 1,
                "&:hover .remove-button": {
                  opacity: 1,
                },
              }}
            >
              <Tooltip
                title={`${image.fileName || "Pasted image"} (${formatFileSize(
                  image.size,
                )})`}
                placement="top"
              >
                <Box
                  component="img"
                  data-testid={`pasted-image-thumbnail-${index}`}
                  src={image.previewUrl}
                  alt={image.fileName || "Pasted image"}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Tooltip>

              {/* Remove button */}
              <IconButton
                className="remove-button"
                size="small"
                data-testid={`pasted-image-remove-btn-${index}`}
                data-attr="assistant:btn:remove-pasted-image"
                onClick={() => onRemove(image.id)}
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  bgcolor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  padding: "2px",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>

              {/* Image icon indicator */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 2,
                  left: 2,
                  bgcolor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: 0.5,
                  padding: "1px 3px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ImageIcon sx={{ fontSize: 12, color: "white" }} />
              </Box>
            </Box>
          ))}
          {Array.from({ length: pendingCount }, (_, index) => (
            <Box
              key={`pending-${index}`}
              data-testid={`pasted-image-pending-${index}`}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                border: "2px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={20} />
            </Box>
          ))}
        </Stack>
      )}

      {pendingCount > 0 && (
        <Typography
          data-testid="pasted-images-pending"
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Processing {pendingCount} image{pendingCount !== 1 ? "s" : ""}...
        </Typography>
      )}

      {images.length > 0 && (
        <Typography
          data-testid="pasted-images-count"
          variant="caption"
          color="text.secondary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <ImageIcon sx={{ fontSize: 14 }} />
          {images.length} image{images.length !== 1 ? "s" : ""} attached - will
          be analyzed by vision model
        </Typography>
      )}
    </Box>
  );
};

export default PastedImagesPreview;
